// TypeScript types mirroring the GraphQL schema in
// assets/appsync/schema.graphql. Hand-maintained for Phase 1 (small
// surface, 4 pages). If the schema grows past ~20 types, run
// graphql-codegen and replace this file with generated output.

export type ApplicationStatus =
  | "CREATED"
  // Send-form intake states (Track 2 send-form workflow). Inserted
  // before the existing SUBMITTED state. Operator-fills-form path
  // skips these and goes CREATED -> SUBMITTED directly.
  | "LINK_SENT"
  | "LINK_OPENED"
  | "FORM_IN_PROGRESS"
  | "READY_FOR_REVIEW"
  | "REVOKED"
  | "SUBMITTED"
  | "SIGNATURE_SENT"
  | "MNSP_SIGNED"
  | "IN_REVIEW"
  | "NEEDS_INFO"
  | "APPROVED"
  | "BOARDED"
  | "REJECTED"
  | "ERROR"
  | "ABANDONED"
  | "ARCHIVED";

// INTERNAL_SALES: Payment Products sales/admin starts a partial application;
// the link goes to the merchant (recipientEmail) to finish it.
export type IntakeRecipientType = "MERCHANT" | "TECH" | "BOTH" | "INTERNAL_SALES";

export type PdiOrderStatus =
  | "NOT_SENT"
  | "PROCESSING"
  | "SENT"
  | "DRY_RUN"
  | "FAILED"
  | "SKIPPED";

export type TeamRole = "EMPLOYEE" | "TECH";

// updateApplication's optional CardConnect side effect (edit-modal buttons).
// NONE = "Save changes" (DB only); RESEND_SIGNATURE = "Save & resend
// CardConnect signature"; SUBMIT = "Save & submit to CardConnect".
export type CardConnectEditAction = "NONE" | "RESEND_SIGNATURE" | "SUBMIT";

// EMPLOYEE-only confirmation-only last-4 of the sensitive leaves an operator
// types once and never sees echoed back (redacted to last-4 at write time).
// Read from MerchantRaw so it reflects what truly persisted through the
// recipient's edits; falls back to the redacted Merchant last-4 once
// MerchantRaw is cleared post-boarding (#130). Each value is a 4-char string
// or "" when unknown. Null on the Application when the caller is a TECH.
export interface PersistedSensitiveLast4 {
  taxId: string;
  ownerSsn: string;
  ownerDriversLicenseNumber: string;
  depositAccountNumber: string;
  depositRoutingNumber: string;
  withdrawalAccountNumber: string;
  withdrawalRoutingNumber: string;
  // "RAW" = confirmed from MerchantRaw; "REDACTED" = only the post-boarding
  // Merchant last-4 remains.
  source: "RAW" | "REDACTED";
}

export interface Application {
  applicationID: string;
  status: ApplicationStatus;
  // Defaults to BUNDLED for rows written before this field existed.
  applicationPath?: ApplicationPath | null;
  merchantEmail: string;
  businessName: string;
  templateID?: string | null;
  // Operator-selected CardConnect sales code / portfolio (#64). Null when
  // the row is on Auto (backend routes by volume threshold). Mirrors the
  // stored RequestedSalesCode. See lib/salesCode.ts.
  salesCode?: string | null;
  cardConnectMerchantID?: string | null;
  signatureUrl?: string | null;
  frontEndMid?: string | null;
  pollAttempts?: number | null;
  nextPollAfter?: string | null;
  errorDetails?: string | null;
  // True only for an ERROR row that never reached CardConnect at all (no
  // cardConnectMerchantID) AND has no raw identity data staged server-side
  // for it -- the edit modal must require the operator retype SSN/tax
  // ID/bank account+routing before a retry can succeed. See
  // needsIdentityReentry in schema.graphql for the full rationale.
  needsIdentityReentry?: boolean | null;
  pdiOrderStatus?: PdiOrderStatus | null;
  pdiOrderSentAt?: string | null;
  pdiOrderError?: string | null;
  // Dry-run marker. New rows carry pdiOrderStatus="DRY_RUN"; this bool stays
  // true on them for one release for backward-compat, and also flags legacy
  // rows written before the DRY_RUN status existed (pdiOrderStatus="SENT" +
  // pdiOrderDryRun=true). The UI treats either shape as a dry run so an
  // operator never reads a suppressed order as a live PDI send.
  pdiOrderDryRun?: boolean | null;
  pdiPrefs?: PdiPrefs | null;
  // Send-form intake meta. All optional — populated only when the
  // operator dispatched the form to a recipient (vs. filling it
  // themselves). Token itself is intentionally NOT exposed on this
  // type; operators see counts and progress, not the secret.
  intakeRecipientType?: IntakeRecipientType | null;
  intakeRecipientEmail?: string | null;
  intakeCcEmail?: string | null;
  intakeTokenExpiresAt?: string | null;
  intakeFirstOpenedAt?: string | null;
  intakeLastOpenedAt?: string | null;
  intakeOpensCount?: number | null;
  intakeProgressPct?: number | null;
  intakeRevoked?: boolean | null;
  intakeSubmittedAt?: string | null;
  // When true, recipient submission triggers an async auto-board to
  // CardConnect (operator review is skipped). Server defaults to null
  // when the dispatch didn't opt in.
  autoSubmit?: boolean | null;
  // MNSP enrollment meta. mnspEnrollmentRequested mirrors the operator's
  // mnspBundle dispatch flag; the form surfaces additional MNSP
  // sections when true. The captured data lands in mnspEnrollment after
  // recipient submit, and the rendered MAR + EUSA PDF is stashed at
  // mnspEnrollmentPdfKey (S3 object key — signed URL generated on
  // dashboard download).
  mnspEnrollmentRequested?: boolean | null;
  // Independent of mnspEnrollmentRequested -- mirrors the operator's
  // terminalRentalBundle dispatch flag. Either or both may be true.
  terminalRentalRequested?: boolean | null;
  mnspEnrollment?: MnspEnrollment | null;
  mnspEnrollmentSubmittedAt?: string | null;
  mnspEnrollmentPdfKey?: string | null;
  // Monthly volume the merchant entered on the intake form. Used by
  // the dashboard to derive + display the matching pricing tier via
  // selectPricingTier() from lib/pricingTiers. Null on rows that
  // pre-date the field or had no volume entered.
  averageMonthlyVolume?: number | null;
  highTicketAmount?: number | null;
  averageTicketAmount?: number | null;
  assignedTechEmail?: string | null;
  assignedToPIN?: string | null;
  createdAt: string;
  updatedAt: string;
  // The status the row held immediately before being archived. Read by
  // the "Unarchive" action to restore it; stale once the row is no
  // longer ARCHIVED.
  preArchiveStatus?: ApplicationStatus | null;
  storeType?: string | null;
  // ----- Additional business/ownership/banking detail ------------------
  // Mirrors the non-sensitive subset of the stored Merchant blob (see
  // sharedModules.redact_merchant_for_storage). Sensitive leaves (owner
  // SSN, driver's license number, tax ID, bank account/routing numbers)
  // are never stored raw and have no field here -- see Owner/BankAccount.
  dbaName?: string | null;
  taxFilingName?: string | null;
  taxFilingMethod?: string | null;
  businessDescription?: string | null;
  website?: string | null;
  businessStartDate?: string | null;
  contactPhone?: string | null;
  mcc?: string | null;
  businessAddress?: Address | null;
  mailingAddress?: Address | null;
  owner?: Owner | null;
  bankDetail?: BankDetail | null;
  persistedSensitiveLast4?: PersistedSensitiveLast4 | null;
  modeOfTransaction?: TransactionMode | null;
  // ----- Admin-only pricing overrides -----------------------------------
  // feeOverrides/icPlusPricingOverrides/cloverSecurityAndTransarmor/
  // fleetOverrides come back as AWSJSON (an opaque JSON blob on the wire,
  // and in practice arrives double-JSON-encoded -- see parseMaybeJson in
  // EditApplicationModal.tsx) but the shape matches FeeOverridesInput/
  // IcPlusPricingOverridesInput/CloverSecurityAndTransarmorInput/
  // FleetOverridesInput field-for-field, so those interfaces are reused
  // here for the read side too rather than declaring separate output types.
  authFeeOverride?: number | null;
  feeOverrides?: FeeOverridesInput | null;
  icPlusPricingOverrides?: IcPlusPricingOverridesInput | null;
  cloverSecurityAndTransarmor?: CloverSecurityAndTransarmorInput | null;
  fleetOverrides?: FleetOverridesInput | null;
  // Annual security bundle fee override ($). Persisted to our DB only (no
  // CardConnect API field); null -> the $199 tier default. See
  // ANNUAL_SECURITY_BUNDLE_FEE_DEFAULT in lib/pricingTiers.ts.
  annualSecurityBundleFee?: number | null;
  // Operator-only free-text notes (pricing context, special arrangements,
  // etc.) attached directly to the application row -- unlike the intake
  // email's one-time personalMessage, this is stored, editable after
  // creation, and never surfaced to the merchant (not part of the
  // intake form, the email, or any CardConnect payload). Issue #130.
  internalNotes?: string | null;
}

// Mirrors the GraphQL SendIntakeLinkInput.
// BUNDLED (default): boards with CardConnect, optionally with MNSP
// enrollment bundled in. MNSP_ENROLLMENT_ONLY: communicates only the MAR
// + Enrollment Agreement and is never submitted to CardConnect.
export type ApplicationPath = "BUNDLED" | "MNSP_ENROLLMENT_ONLY";

export interface SendIntakeLinkInput {
  recipientType: IntakeRecipientType;
  recipientEmail: string;
  ccEmail?: string;
  expiresInDays?: number;
  personalMessage?: string;
  // Auto-board to CardConnect on recipient submit (skip operator review).
  // Optional; defaults to false server-side.
  autoSubmit?: boolean;
  // Bundle the MNSP enrollment (MAR + End User Agreement) with
  // this intake. When true, the form surfaces additional sections for
  // equipment, pricing, and per-section acceptance; recipient cannot
  // submit until all four AgreementSection values are accepted.
  // Default false = CardConnect-only application.
  mnspBundle?: boolean;
  // Independent of mnspBundle -- requests terminal equipment rental
  // without necessarily enrolling in the MNSP network. Either or both
  // may be true. Default false = no rental.
  terminalRentalBundle?: boolean;
  // Default BUNDLED. MNSP_ENROLLMENT_ONLY forces mnspBundle=true
  // server-side and the row never boards to CardConnect.
  applicationPath?: ApplicationPath;
  // Operator-set terminal monthly rental ($/month) for the rental
  // equipment. Only sent with terminalRentalBundle=true; persisted to
  // the row's mnspEnrollment so the merchant sees it read-only and the
  // MAR PDF renders the Equipment Share Terms (issue #86).
  terminalMonthlyRentalFee?: number;
  gatewayName?: string;
  // Merchant fields the operator pre-filled (merchantData dotted paths); the
  // intake form shows them read-only and the API strips them from merchant
  // saves. See lib/intakeLocks.ts.
  lockedFields?: string[];
}

// ----- MNSP enrollment (mirrors the GraphQL MnspEnrollment types) ----------
// See assets/appsync/schema.graphql + docs/reference/cybera-mar-enrollment-fields.md
// for the source-PDF field mapping.

export type MnspServiceType =
  | "GILBARCO_MNSP"
  | "VERIFONE_MNSP"
  | "BULLOCH_MNSP"
  // Oddball / small-market registers outside the big three. Carries no
  // fixed price (registerOther holds the free-text name); the rep
  // confirms pricing manually. See mnspPricing.ts.
  | "OTHER_MNSP";

export type MnspShippingSpeed =
  | "STANDARD"
  | "ONE_DAY"
  | "TWO_DAY"
  // Legacy: no longer offered (TWO_DAY replaced it), kept for saved rows.
  | "THREE_DAY"
  | "FIVE_DAY";

export type AgreementSection =
  | "MNSP_BILLING"
  | "TERMINAL_RENTAL"
  | "ACH_AUTH"
  | "CANCELLATION_TERMS";

export interface AgreementAcceptance {
  section: AgreementSection;
  acceptedAt?: string | null;
  acceptedBy?: string | null;
}

export type AgreementAcceptanceInput = AgreementAcceptance;

/** Forensic signature metadata captured server-side at the moment
 * the recipient's first PATCH includes a non-empty signatureImage.
 * Null on older rows (pre-forensics) or rows where the operator
 * filled the form (no recipient signing event).
 */
export interface MnspSignatureMeta {
  ip?: string | null;
  userAgent?: string | null;
  signedAt?: string | null;
  lastPatchIp?: string | null;
}

export interface MnspEnrollment {
  serviceType?: MnspServiceType | null;
  /** Free-text register name when serviceType is OTHER_MNSP. Null for the
   *  three fixed-price registers. */
  registerOther?: string | null;
  /** In-house PP terminal rental, $/month. Month-to-month, six-month
   *  minimum (Michael 2026-07-13). Operator-set only. */
  terminalMonthlyRentalFee?: number | null;
  /** Operator override of the standard per-register deployment/monthly
   *  fees (e.g. a waived deployment fee or a custom monthly rate).
   *  Null means "use the standard per-register fee" -- see mnspPricing.ts
   *  getEffectiveMnspFees. Operator-set only. */
  deploymentFeeOverride?: number | null;
  monthlyFeeOverride?: number | null;
  gatewayName?: string | null;
  terminalType?: string | null;
  pinPad?: string | null;
  internetGatewayProvided?: boolean | null;
  shippingSpeed?: MnspShippingSpeed | null;
  /** Operator override of the shipping fee. Null means "use the standard
   *  per-speed fee." Operator-set only. SECURITY: only applies when
   *  shippingSpeed === shippingFeeOverrideSpeed (the exact speed this
   *  was set for) -- otherwise a merchant could switch to a pricier
   *  expedited speed and still pay a cheaper day's override amount. */
  shippingFeeOverride?: number | null;
  /** The shipping speed this override was set for -- see
   *  shippingFeeOverride. Operator-set only. */
  shippingFeeOverrideSpeed?: MnspShippingSpeed | null;
  /** Base64 PNG data URL of the merchant's hand-drawn signature. */
  signatureImage?: string | null;
  acceptances?: AgreementAcceptance[] | null;
  /** Read-only on the dashboard: signing IP/UA/timestamp. */
  signatureMeta?: MnspSignatureMeta | null;
  /** SHA-256 hex of the rendered MAR/EUSA PDF. Set after the
   * cardConnect render+stash step. Null until the row reaches that
   * point of the lifecycle. */
  pdfSha256?: string | null;
}

/** Mutation input (no signatureMeta/pdfSha256 -- those are server-set). */
export type MnspEnrollmentInput = Omit<
  MnspEnrollment,
  "signatureMeta" | "pdfSha256"
>;

// Which AgreementSection acceptances apply, given which of MNSP network
// enrollment / terminal rental is active (either, both, or neither).
// Mirrors intakeApiHandler.py's _required_agreement_sections exactly:
// MNSP_BILLING only when MNSP is enabled, TERMINAL_RENTAL only when
// rental is enabled, and ACH_AUTH/CANCELLATION_TERMS (general billing/
// cancellation terms) whenever either is enabled.
export function requiredAgreementSections(
  mnspEnabled: boolean,
  rentalEnabled: boolean,
): AgreementSection[] {
  const sections: AgreementSection[] = [];
  if (mnspEnabled) sections.push("MNSP_BILLING");
  if (rentalEnabled) sections.push("TERMINAL_RENTAL");
  if (mnspEnabled || rentalEnabled) sections.push("ACH_AUTH", "CANCELLATION_TERMS");
  return sections;
}

// Operator-collected PDI/equipment preferences. Every field is
// optional — the future Track 3 handler supplies defaults when
// reading. Mirrors the GraphQL PdiPrefs type.
export interface PdiPrefs {
  // "No" suppresses the PDI managed-network order entirely (no email to PDI
  // when the merchant signs), regardless of applicationPath. "" / "Yes" /
  // null all mean "send the order" -- the default and what pre-existing rows do.
  sendPdiOrder?: string | null;
  posPlatform?: string | null;
  singleOrMultiSite?: string | null;
  gasPumpYn?: string | null;
  gasPumpType?: string | null;
  broadbandYn?: string | null;
  managedWifiInterest?: string | null;
  residentialLocation?: string | null;
  shippingSpeed?: string | null;
  notes?: string | null;
}

export interface AuditLogEntry {
  eventID: string;
  timestamp: string;
  eventType: string;
  applicationID?: string | null;
  actor?: string | null;
  detail?: unknown;
}

export interface TeamUser {
  email: string;
  role: TeamRole | string; // server returns lowercase "employee" | "tech" too
  assignedTechId?: string | null;
}

export interface SigninResult {
  status: "success" | "failed";
  errorCode?: string | null;
  message?: string | null;
  jwt?: string | null;
  passwordChangeRequired: boolean;
  user?: TeamUser | null;
}

export interface PasswordChangeResult {
  status: "success" | "failed";
  errorCode?: string | null;
  message?: string | null;
  jwt?: string | null;
}

// Mutation.createApplication argument shapes. Mirror the expanded
// MerchantDataInput in assets/appsync/schema.graphql (full hosted-intake).
// Fields marked SENSITIVE are submitted to CardConnect but never persisted
// raw — the backend stores only a masked last-4.
export interface AddressInput {
  address1?: string;
  address2?: string;
  city?: string;
  stateCd?: string;
  zip?: string;
  countryCd?: string;
}

export interface OwnerInput {
  firstName?: string;
  lastName?: string;
  title?: string;
  email?: string;
  phone?: string;
  ownerMobilePhone?: string;
  ownershipTypeCd?: string;
  ssn?: string; // SENSITIVE
  dateOfBirth?: string; // MM/DD/YYYY
  ownershipPct?: number;
  driversLicenseNumber?: string;
  driversLicenseStateCd?: string;
  address?: AddressInput;
}

export interface BankAccountInput {
  accountNumber?: string; // SENSITIVE
  routingNumber?: string; // SENSITIVE
  accountType?: string;
  bankName?: string;
}

export interface BankDetailInput {
  deposit?: BankAccountInput;
  withdrawal?: BankAccountInput;
}

export interface TransactionModeInput {
  eCommercePct?: number;
  keyedPct?: number;
  mailOrderPct?: number;
  swipedPct?: number;
}

// ----- Read-side shapes for the additional business/ownership/banking
// detail on Application (mirrors the non-sensitive subset of the stored
// Merchant blob -- see sharedModules.redact_merchant_for_storage).
// Distinct from the *Input types above: Owner/BankAccount deliberately
// omit ssn/driversLicenseNumber/accountNumber/routingNumber since those
// are redacted to a masked last-4 at write time and never surfaced.
export type Address = AddressInput;

export interface Owner {
  firstName?: string | null;
  lastName?: string | null;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  ownerMobilePhone?: string | null;
  ownershipTypeCd?: string | null;
  ownershipPct?: number | null;
  dateOfBirth?: string | null;
  driversLicenseStateCd?: string | null;
  address?: Address | null;
}

export interface BankAccount {
  accountType?: string | null;
  bankName?: string | null;
}

export interface BankDetail {
  deposit?: BankAccount | null;
  withdrawal?: BankAccount | null;
}

export interface TransactionMode {
  eCommercePct?: number | null;
  keyedPct?: number | null;
  mailOrderPct?: number | null;
  swipedPct?: number | null;
}

export interface VolumeDetailsInput {
  averageMonthlyVolume?: number;
  highTicketAmount?: number;
  averageTicketAmount?: number;
}

export interface DeliveryPercentagesInput {
  dlvrySameDayPct?: number;
  dlvry0To7DaysPct?: number;
  dlvry8To14DaysPct?: number;
  dlvry15To30DaysPct?: number;
  dlvryOver30DaysPct?: number;
}

export interface MerchantDataInput {
  businessName: string;
  dbaName?: string;
  businessStartDate?: string;
  taxFilingName?: string;
  taxFilingMethod?: string; // "EIN" | "SSN"
  taxID?: string; // SENSITIVE
  mcc?: string;
  storeType?: string;
  businessDescription?: string;
  website?: string;
  contactEmail: string;
  contactPhone?: string;
  volumeDetails?: VolumeDetailsInput;
  deliveryPercentages?: DeliveryPercentagesInput;
  businessAddress?: AddressInput;
  mailingAddress?: AddressInput;
  owner?: OwnerInput;
  bankDetail?: BankDetailInput;
  modeOfTransaction?: TransactionModeInput;
  // Present when the operator flagged the intake as mnspBundle=true
  // and/or terminalRentalBundle=true. Null on plain CardConnect-only
  // applications.
  mnspEnrollment?: MnspEnrollmentInput | null;
}

// Mirrors the GraphQL PdiPrefsInput. All optional — form supplies
// sensible defaults so in practice the block is always sent populated.
export type PdiPrefsInput = PdiPrefs;

// Whether a PDI managed-network order is (or would be) sent for this
// application: an explicit sendPdiOrder wins, else the path-aware default --
// a BUNDLED application orders by default (opt-out), an MNSP-enrollment-only
// application doesn't (opt-in). Mirrors pdiOrderHandler._pdi_order_wanted
// server-side. Shared by ApplicationDetail (pdiOrderInPlay -- hides the PDI
// cards when false), IntakeForm (pdiOrderInPlay -- hides the merchant-facing
// PDI section), and PdiFields (resolves the toggle's displayed Yes/No) so
// the three can't silently drift out of sync.
export function pdiOrderWanted(
  sendPdiOrder: string | null | undefined,
  enrollmentOnly: boolean,
): boolean {
  return sendPdiOrder === "Yes"
    ? true
    : sendPdiOrder === "No"
      ? false
      : !enrollmentOnly;
}

export interface CreateApplicationInput {
  templateID: string;
  // Optional operator-selected sales code / portfolio (#64). Omitted =
  // backend defaults it by the pricing threshold. See lib/salesCode.ts.
  salesCode?: string;
  // Optional per-application operator override of the per-authorization
  // fee (special-merchant pricing). Dollars, range 0..10; layered onto the
  // volume-derived fees server-side. Omitted = pure tiered pricing.
  authFeeOverride?: number;
  // Admin-only manual overrides for every other fee on the tier's `fees`
  // block (see lib/pricingTiers.ts FEE_OVERRIDE_FIELDS). Any field omitted
  // here falls back to the tier default; the backend layers whatever is
  // present on top of the volume-derived fees at submit. Never surfaced to
  // the merchant-facing intake form.
  feeOverrides?: FeeOverridesInput;
  // Admin-only manual overrides for the icPlusPricing block sent alongside
  // the merchant. Any field omitted falls back to the volume-derived SSTHUB
  // template default. Never surfaced to the merchant-facing intake form.
  icPlusPricingOverrides?: IcPlusPricingOverridesInput;
  // Admin-only overrides for the tier's Clover Security + TransArmor block
  // (see lib/pricingTiers.ts CLOVER_SECURITY_AND_TRANSARMOR_FIELDS). A
  // THIRD parallel object to feeOverrides/icPlusPricingOverrides -- unlike
  // fleetOverrides below, this DOES reach the CardConnect payload
  // (merchant.cloverSecurityAndTransarmor), layered on top of the tier
  // defaults at submit. Never surfaced to the merchant-facing intake form.
  cloverSecurityAndTransarmor?: CloverSecurityAndTransarmorInput;
  // Admin-only overrides for the tier's fleet-card pricing block (see
  // lib/pricingTiers.ts FLEET_OVERRIDE_FIELDS). SEPARATE parallel object to
  // feeOverrides: fleet overrides are validated + persisted for future
  // MAR/display use only and MUST NEVER reach the CardConnect payload (the
  // CoPilot API rejects Voyager/WEX/EBT/PIN-debit fields). Omitted = tier
  // fleet defaults.
  fleetOverrides?: FleetOverridesInput;
  // Operator override of the Annual security bundle fee ($). Persisted to our
  // DB only (row attr AnnualSecurityBundleFee); CardConnect has no API field
  // for it, so -- like fleetOverrides -- it never reaches the boarding
  // payload. Omitted = the $199 tier default.
  annualSecurityBundleFee?: number;
  // Operator-only free-text notes (pricing context, special arrangements,
  // etc.) attached directly to the application row. Never surfaced to the
  // merchant or CardConnect. Issue #130.
  internalNotes?: string;
  merchantData: MerchantDataInput;
  assignedTechEmail?: string;
  registeringFirstName?: string;
  registeringLastName?: string;
  registeringPhone?: string;
  pdiPrefs?: PdiPrefsInput;
  assignedToPIN?: string;
  // When true, the handler generates an intake token so the merchant
  // can sign the MNSP section from the intake form. A second button
  // is included in the CardConnect signature email pointing to the
  // intake link.
  mnspBundle?: boolean;
  // Independent of mnspBundle -- requests terminal equipment rental
  // without necessarily enrolling in the MNSP network. Either or both
  // may be true. Default false = no rental.
  terminalRentalBundle?: boolean;
  // Default BUNDLED. MNSP_ENROLLMENT_ONLY forces mnspBundle=true
  // server-side and the row never boards to CardConnect.
  applicationPath?: ApplicationPath;
}

// Admin-only manual fee overrides layered onto the tier defaults. Mirrors
// PricingTier["fees"] in lib/pricingTiers.ts minus authFee (which keeps its
// own dedicated authFeeOverride field above for backward compatibility with
// the existing single-field override wiring). Every field optional — the
// backend only overrides what's present.
export interface FeeOverridesInput {
  achBatchFee?: number;
  addressVerifFee?: number;
  annualMembershipFee?: number;
  appFee?: number;
  chargebackFee?: number;
  cloverPlatformFee?: number;
  cloverSecurityFee?: number;
  ddaRejectFee?: number;
  earlyCancelFee?: number;
  minProcessFee?: number;
  monthlyEquipmentRentalFee?: number;
  pciAnnualFee?: string;
  pciConciergeMonthlyFee?: number;
  pciNonComplianceFee?: number;
  pciProgramCd?: "CONCIERGE" | "ANNUAL";
  regProdMonthlyFee?: number;
  retrievalFee?: number;
  statementFee?: number;
  transactionFee?: number;
  voiceAuthFee?: number;
  wirelessActivationFee?: number;
  wirelessFee?: number;
  duesAndAssessmentsFlg?: boolean;
  passthruInterchgCostsFlg?: boolean;
}

// Admin-only manual overrides for the icPlusPricing block sent alongside
// the merchant (interchangeTypeCode + the four qualified-discount
// percentages). Layered on top of the volume-derived SSTHUB template
// defaults server-side; that table is never altered by an override. Every
// field optional — an all-omitted input is byte-for-byte the volume-derived
// block.
export interface IcPlusPricingOverridesInput {
  interchangeTypeCode?: "GROSS" | "NET";
  amexOptBlueQualDiscountPct?: number;
  discoverQualCreditDiscountPct?: number;
  mastercardQualCreditDiscountPct?: number;
  visaQualCreditDiscountPct?: number;
}

// Admin-only overrides for the tier's Clover Security + TransArmor block. A
// THIRD parallel object to FeeOverridesInput/IcPlusPricingOverridesInput
// (own dict, own DynamoDB attribute) even though -- unlike FleetOverridesInput
// below -- this DOES reach the CardConnect payload (merchant.
// cloverSecurityAndTransarmor), same as fees/icPlusPricing. Mirrors
// CLOVER_SECURITY_AND_TRANSARMOR_FIELDS in lib/pricingTiers.ts. Every field
// optional — an all-omitted input is byte-for-byte the tier default block.
export interface CloverSecurityAndTransarmorInput {
  transarmorDataProtectionFlg?: boolean;
  transarmorTokenTypeCd?: string;
  transarmorMultiPayToken?: string;
  transarmorMonthlyFee?: number;
  cloverSecurityBundleCd?: string;
  cloverSecurityPlusFee?: number;
  pciComplianceServiceFee?: number;
  pciComplianceServiceFeeFrequency?: number;
  pciComplianceServiceFeeMonthToBill?: string;
}

// Admin-only overrides for the tier's fleet-card pricing block. SEPARATE
// parallel object to FeeOverridesInput: these fields are validated +
// persisted for future MAR/display use only and MUST NEVER reach the
// CardConnect payload. Mirrors FLEET_OVERRIDE_FIELDS in lib/pricingTiers.ts.
// Every field optional — the backend only overrides what's present.
// ebtFnsNumber is the merchant's USDA Food and Nutrition Service number for
// EBT: an identifier, not a fee. It's a String of exactly 7 digits whose
// leading/trailing zeros are significant ("0712345"), so it is String, not a
// number: a number would drop leading zeros (see coerce_fns_number in
// pricing_tiers.py, issue #197).
export interface FleetOverridesInput {
  voyagerAuthFee?: number;
  voyagerSalesDiscountPct?: number;
  wexSalesDiscountPct?: number;
  wexTransactionFee?: number;
  wexChargebackFee?: number;
  wexRetrievalFee?: number;
  pinDebitDiscountFee?: number;
  pinDebitTransactionFee?: number;
  ebtFnsNumber?: string;
  ebtTransactionFee?: number;
}

// Mutation.updateApplication input shape. DELIBERATELY a narrower surface
// than CreateApplicationInput/MerchantDataInput: it covers only the fields
// that (a) NewApplication's create form collects AND (b) actually persist
// onto the Application row afterward. pdiPrefs.shippingSpeed is likewise
// not part of this surface -- NewApplication derives it from the MNSP
// bundle selection rather than collecting it directly.
// Owner fields editable via updateApplication. ssn/driversLicenseNumber are
// write-only: both are redacted to a masked last-4 at write time, so the
// modal never has a raw value to prefill -- blank means "leave the stored
// value alone," a non-blank value overwrites both CardConnect's live
// record and the stored last-4.
export interface UpdateOwnerInput {
  firstName?: string;
  lastName?: string;
  title?: string;
  email?: string;
  phone?: string;
  ownerMobilePhone?: string;
  ownershipTypeCd?: string;
  ownershipPct?: number;
  dateOfBirth?: string;
  ssn?: string;
  driversLicenseNumber?: string;
  driversLicenseStateCd?: string;
  address?: AddressInput;
}

// Bank fields editable via updateApplication. accountNumber/routingNumber
// are write-only, same reasoning as UpdateOwnerInput.ssn.
export interface UpdateBankAccountInput {
  accountType?: string;
  bankName?: string;
  accountNumber?: string;
  routingNumber?: string;
}

export interface UpdateBankDetailInput {
  deposit?: UpdateBankAccountInput;
  withdrawal?: UpdateBankAccountInput;
}

export interface UpdateApplicationInput {
  businessName: string;
  merchantEmail: string;
  templateID: string;
  // Operator-selected CardConnect sales code (#64). Always sent and
  // authoritative -- "" = Auto clears any stored override, same posture as
  // the pricing overrides below. Only the sales code reaches the CardConnect
  // merchant update; the paired templateID is persisted but never re-boarded.
  salesCode?: string;
  storeType?: string;
  assignedToPIN?: string;
  assignedTechEmail?: string;
  averageMonthlyVolume?: number;
  highTicketAmount?: number;
  averageTicketAmount?: number;
  pdiPrefs?: PdiPrefsInput;
  // Mirrors NewApplication's send-intake "fill out some of it" optional
  // section (see UpdateOwnerInput/UpdateBankAccountInput above for what's
  // deliberately excluded). All optional -- omitted fields keep their
  // current stored value unchanged.
  dbaName?: string;
  taxFilingName?: string;
  taxFilingMethod?: string;
  taxID?: string; // write-only, same reasoning as UpdateOwnerInput.ssn
  businessDescription?: string;
  website?: string;
  businessStartDate?: string;
  contactPhone?: string;
  mcc?: string;
  businessAddress?: AddressInput;
  mailingAddress?: AddressInput;
  owner?: UpdateOwnerInput;
  bankDetail?: UpdateBankDetailInput;
  modeOfTransaction?: TransactionModeInput;
  // Only meaningful when the application already has an MNSP enrollment
  // and/or terminal rental (mnspEnrollmentRequested / terminalRental
  // Requested); only ever sent when at least one of those is true. Never
  // touches signatureImage/acceptances -- see UpdateMnspEnrollmentInput.
  mnspEnrollment?: UpdateMnspEnrollmentInput;
  // Admin-only pricing overrides (mirrors NewApplication's Manual fee
  // adjustment / Fleet pricing adjustment sections). Unlike most fields
  // here, these are ALWAYS sent and are authoritative -- omitted/blank
  // genuinely means "no override," so the operator can clear a
  // previously-set override through this modal, not just change it.
  authFeeOverride?: number | null;
  feeOverrides?: FeeOverridesInput;
  icPlusPricingOverrides?: IcPlusPricingOverridesInput;
  cloverSecurityAndTransarmor?: CloverSecurityAndTransarmorInput;
  fleetOverrides?: FleetOverridesInput;
  // Annual security bundle fee override ($). Always authoritative like the
  // pricing overrides above -- null clears AnnualSecurityBundleFee so display
  // falls back to the $199 tier default. Never sent to CardConnect.
  annualSecurityBundleFee?: number | null;
  // Operator-only free-text notes. Same "always authoritative" posture as
  // the pricing overrides above -- blank clears a previously-set note
  // rather than leaving it alone. Never surfaced to the merchant or
  // CardConnect. Issue #130.
  internalNotes?: string;
}

// Non-signature MNSP fields editable via updateApplication. Deliberately
// excludes signatureImage and acceptances (present on MnspEnrollmentInput,
// used by the create/send-intake path) -- those come from the merchant's
// actual signing flow, not an operator edit here.
export interface UpdateMnspEnrollmentInput {
  serviceType?: MnspServiceType;
  registerOther?: string;
  terminalMonthlyRentalFee?: number;
  deploymentFeeOverride?: number;
  monthlyFeeOverride?: number;
  gatewayName?: string;
  terminalType?: string;
  pinPad?: string;
  internetGatewayProvided?: boolean;
  shippingSpeed?: MnspShippingSpeed;
  shippingFeeOverride?: number;
  shippingFeeOverrideSpeed?: MnspShippingSpeed;
}

// Per-field server-side validation error. Mirrors the GraphQL FieldError
// type. Populated by the backend when a failure is attributable to one
// or more specific inputs (e.g., CardConnect 400 with field paths) so
// the frontend can render the messages inline rather than as a banner.
export interface ServerFieldError {
  field: string;
  message: string;
}

export interface ApplicationResult {
  status: "success" | "failed";
  errorCode?: string | null;
  message?: string | null;
  application?: Application | null;
  fieldErrors?: ServerFieldError[] | null;
}

// Decoded JWT shape — fields the team auth Lambdas set on issue.
export interface DecodedJwt {
  email: string;
  role?: string;
  scope?: string;
  assignedTechId?: string;
  iat: number;
  exp: number;
}
